import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import NextLink from 'next/dist/client/link';
import { useSelector } from 'react-redux';
import { always } from 'ramda';

import { HOME_ROUTE, LANDMARK_ROLES, ROUTE } from '@utils/constants';
import { footerSelectors } from '@modules/footer';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { useHeaderSublevel } from '@hooks/useUi';
import { SUBLEVEL } from '@components/header/header.constants';
import { useCustomer } from '@hooks/useCustomer';
import { useShippingCountry } from '@hooks/useShipping';
import { NewsletterFooter } from '@components/newsletterFooter';

import {
  Button,
  Container,
  Content,
  FacebookIcon,
  InstagramIcon,
  Link,
  LinkContent,
  LinkItem,
  Links,
  LinksList,
  MainLogo,
  SocialItem,
  SocialLink,
  SocialList,
} from './footer.styles';

export const Footer = () => {
  const intl = useIntl();
  const links = useSelector(footerSelectors.selectFooterLinks);
  const { openHeaderSublevel } = useHeaderSublevel();
  const isFacebook = !!links?.facebook;
  const isInstagram = !!links?.instagram;
  const { isAuthed } = useCustomer();
  const { shippingCountryCode } = useShippingCountry();
  const shippingLabel = intl.formatMessage(
    { id: 'footer.shippingLabel', defaultMessage: 'Shipping to. Currently selected {country}' },
    { country: shippingCountryCode }
  );
  const facebookLabel = intl.formatMessage({ id: 'footer.facebookLinkTitle', defaultMessage: 'Facebook' });
  const instagramLabel = intl.formatMessage({ id: 'footer.instagramLinkTitle', defaultMessage: 'Instagram' });

  const renderFacebookLink = renderWhenTrue(() => (
    <SocialItem>
      <SocialLink href={links?.facebook as string} target="_blank" title={facebookLabel} aria-label={facebookLabel}>
        <FacebookIcon />
      </SocialLink>
    </SocialItem>
  ));

  const renderInstagramLink = renderWhenTrue(() => (
    <SocialItem>
      <SocialLink href={links?.instagram as string} target="_blank" title={instagramLabel} aria-label={instagramLabel}>
        <InstagramIcon />
      </SocialLink>
    </SocialItem>
  ));

  const renderSocialLinks = renderWhenTrue(() => (
    <LinkItem>
      <SocialList>
        {renderFacebookLink(isFacebook)}
        {renderInstagramLink(isInstagram)}
      </SocialList>
    </LinkItem>
  ));

  const renderAccountLink = renderWhenTrueOtherwise(
    always(
      <NextLink href={ROUTE.ACCOUNT} passHref>
        <Link>
          <LinkContent>
            <FormattedMessage id="footer.account" defaultMessage="Account" />
          </LinkContent>
        </Link>
      </NextLink>
    ),
    always(
      <Button onClick={() => openHeaderSublevel({ sublevel: SUBLEVEL.ACCOUNT })}>
        <FormattedMessage id="footer.account" defaultMessage="Account" />
      </Button>
    )
  );

  return (
    <Container role={LANDMARK_ROLES.CONTENT_INFO}>
      <NewsletterFooter />
      <Content>
        <Links>
          <LinksList>
            <LinkItem>
              <NextLink href={HOME_ROUTE} passHref>
                <Link>
                  <MainLogo>
                    <FormattedMessage id="footer.platform" defaultMessage="Platform: " />
                  </MainLogo>
                </Link>
              </NextLink>
            </LinkItem>
          </LinksList>
          <LinksList>
            <LinkItem>
              <NextLink href={ROUTE.ABOUT} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.about" defaultMessage="About Platform" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.FAQ} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.faq" defaultMessage="How It Works & FAQ" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.GALLERIES} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.galleries" defaultMessage="Galleries" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.FEATURES} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.pastEditorial" defaultMessage="Features" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.GIFT_CARD} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.giftCard" defaultMessage="Gift Card" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.TRADE} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.tradeProgram" defaultMessage="Trade Program" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
          </LinksList>

          <LinksList>
            <LinkItem>
              <Button onClick={() => openHeaderSublevel({ sublevel: SUBLEVEL.COUNTRY })} aria-label={shippingLabel}>
                <LinkContent>{shippingCountryCode}, $</LinkContent>
              </Button>
            </LinkItem>
            <LinkItem>{renderAccountLink(isAuthed)}</LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.CONTACT} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.contact" defaultMessage="Contact Us" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.TERMS_AND_CONDITIONS} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.termsAndConditions" defaultMessage="User Terms" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            <LinkItem>
              <NextLink href={ROUTE.PRIVACY_POLICY} passHref>
                <Link>
                  <LinkContent>
                    <FormattedMessage id="footer.privacyPolicy" defaultMessage="Privacy policy" />
                  </LinkContent>
                </Link>
              </NextLink>
            </LinkItem>
            {renderSocialLinks(isFacebook || isInstagram)}
          </LinksList>
        </Links>
      </Content>
    </Container>
  );
};
